import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
  createMessage,
  addComentario,
  resetSiscotelFlag,
} from "../../../slices/thunks";
import { solicitudSuccess } from "../../../slices/configuracion/solicitudes/reducer";
import { APIClient, marcarComentariosLeidos } from "../../../helpers/api_helper";
import { getUrlPrefirmada } from "../../../helpers/backend_helper";
import {
  Card,
  CardBody,
  Col,
  Label,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import FileViewer from "../../../Components/Common/FileViewer";

// Estilos para la animación de giro
const spinStyle = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .spin {
    animation: spin 1s linear infinite;
    display: inline-block;
  }
`;

// Primero, agregar las funciones de utilidad al inicio del archivo
const compressImage = async (base64String) => {
  try {
    // Crear una imagen desde el base64
    const img = new Image();

    // Crear una promesa para manejar la carga de la imagen
    const imageLoadPromise = new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = base64String;
    });

    // Esperar a que la imagen cargue
    const loadedImg = await imageLoadPromise;

    // Crear un canvas para la compresión
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Calcular las dimensiones máximas
    const MAX_WIDTH = 800;
    const scaleFactor = MAX_WIDTH / loadedImg.width;
    canvas.width = MAX_WIDTH;
    canvas.height = loadedImg.height * scaleFactor;

    // Dibujar la imagen en el canvas con las nuevas dimensiones
    ctx.drawImage(loadedImg, 0, 0, canvas.width, canvas.height);

    // Convertir el canvas a base64 con compresión
    const compressedBase64 = canvas.toDataURL("image/jpeg", 0.7);

    return compressedBase64;
  } catch (error) {
    console.error("Error en la compresión de imagen:", error);
    throw new Error(`Error al comprimir la imagen: ${error.message}`);
  }
};

const cleanBase64 = (base64String) => {
  if (!base64String) return "";
  const match = base64String.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
  return match ? match[2] : base64String;
};

const Comentarios = ({ solicitud }) => {
  const dispatch = useDispatch();
  const [mensaje, setMensaje] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [adjunto, setAdjunto] = useState(null);
  const [loadingx, setLoadingx] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState({
    url: null,
    name: "",
    type: ""
  });

  // Refs para mantener los valores actuales sin causar re-renderizados
  const mensajeRef = useRef("");
  const adjuntoRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Sincronizar refs con estado
  useEffect(() => {
    mensajeRef.current = mensaje;
  }, [mensaje]);

  useEffect(() => {
    adjuntoRef.current = adjunto;
  }, [adjunto]);

  // Agregar el estilo al DOM cuando el componente se monta
  useEffect(() => {
    // Crear un elemento de estilo
    const styleElement = document.createElement('style');
    styleElement.innerHTML = spinStyle;
    document.head.appendChild(styleElement);

    // Limpiar cuando el componente se desmonta
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Agrega estas funciones para manejar la visualización de archivos
  const handleOpenFile = (adjunto) => {
    if (!adjunto) {
      toast.error("No hay archivo adjunto disponible");
      return;
    }
    
    //console.log("Comentarios - Abriendo archivo:", adjunto);
    
    if (adjunto.startsWith('http')) {
      // Usar URL prefirmada para archivos en DO Spaces
      //console.log("Comentarios - Obteniendo URL prefirmada para:", adjunto);
      getUrlPrefirmada(adjunto)
        .then(response => {
          //console.log("Comentarios - Respuesta URL prefirmada:", response.data);
          if (response.data && response.data.url) {
            const prefirmedUrl = response.data.url;
            const fileType = isPdfFile(adjunto) ? "pdf" : isImageFile(adjunto) ? "image" : "other";
            
            //console.log("Comentarios - URL prefirmada obtenida:", prefirmedUrl);
            //console.log("Comentarios - Tipo de archivo detectado:", fileType);
            
            setCurrentFile({
              url: prefirmedUrl,
              name: "Archivo adjunto",
              type: fileType
            });
            setFileViewerOpen(true);
          } else {
            //console.error("Comentarios - Error en respuesta:", response);
            toast.error("No se pudo obtener la URL prefirmada");
          }
        })
        .catch(error => {
          //console.error("Comentarios - Error al obtener URL prefirmada:", error);
          toast.error("Error al obtener la URL prefirmada");
        });
    } else if (isImageFile(adjunto) || isPdfFile(adjunto)) {
      const fileType = isPdfFile(adjunto) ? "pdf" : "image";
      //console.log("Comentarios - Usando datos base64 directamente, tipo:", fileType);
      
      // Para datos base64, verificar si necesita procesamiento
      let processedUrl = adjunto;
      
      // Si es base64 de imagen o PDF, asegurarse de que tenga el formato correcto
      if (adjunto.includes(";base64,") && !adjunto.startsWith("data:")) {
        // Agregar el prefijo data: si falta
        const mimeType = isPdfFile(adjunto) ? "application/pdf" : "image/jpeg";
        processedUrl = `data:${mimeType};base64,${adjunto.split(";base64,")[1]}`;
        //console.log("Comentarios - URL procesada con prefijo MIME");
      }
      
      setCurrentFile({
        url: processedUrl,
        name: "Archivo adjunto",
        type: fileType
      });
      setFileViewerOpen(true);
    } else {
      //console.log("Comentarios - Formato no soportado:", adjunto.substring(0, 50) + "...");
      toast.error("Formato de archivo no soportado");
    }
  };

  const isImageFile = (fileData) => {
    if (!fileData) return false;
    
    // Si es una URL
    if (fileData.startsWith('http')) {
      const extension = fileData.split('?')[0].toLowerCase();
      return extension.endsWith('.jpg') || 
             extension.endsWith('.jpeg') || 
             extension.endsWith('.png') || 
             extension.endsWith('.gif') || 
             extension.endsWith('.webp') ||
             fileData.includes('/image/');
    }
    
    // Si es base64
    return fileData.includes("data:image/") || 
          (fileData.includes("base64") && !fileData.includes("JVBERi0"));
  };
  
  const isPdfFile = (fileData) => {
    if (!fileData) return false;
    
    // Si es una URL
    if (fileData.startsWith('http')) {
      const extension = fileData.split('?')[0].toLowerCase();
      return extension.endsWith('.pdf') || 
             fileData.includes('/pdf/') ||
             fileData.includes('content-type=application%2Fpdf');
    }
    
    // Si es base64
    return fileData.includes("data:application/pdf") || 
           fileData.includes("JVBERi0"); // Firma de PDF en base64
  };

  // Función para crear un Blob URL a partir de datos base64
  const createBlobUrl = (base64Data) => {
    // Validar que sea una cadena y que tenga el formato correcto
    if (!base64Data || typeof base64Data !== 'string') {
      //console.error('Datos inválidos:', base64Data);
      return null;
    }

    try {
      // Verificar si es una cadena base64 válida con formato data:image/...;base64,...
      if (!base64Data.includes(';base64,')) {
        //console.error('Formato base64 inválido:', base64Data);
        return null;
      }

      const parts = base64Data.split(';base64,');
      const contentType = parts[0].split(':')[1];
      const raw = window.atob(parts[1]);
      const rawLength = raw.length;
      const uInt8Array = new Uint8Array(rawLength);

      for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
      }

      const blob = new Blob([uInt8Array], { type: contentType });
      return URL.createObjectURL(blob);
    } catch (error) {
        //console.error('Error al crear Blob URL:', error);
      return null;
    }
  };

  const selectLayoutState = (state) => state;
  const pageData = createSelector(selectLayoutState, (state) => ({
    siscotel: state.Siscotel.siscotel,
    error: state.Siscotel.error,
    loading: state.Siscotel.loading,
    errorMsg: state.Siscotel.errorMsg,
    colorMsg: state.Siscotel.colorMsg,
    exito: state.Siscotel.exito,
  }));

  const { siscotel, error, loading, errorMsg, colorMsg, exito } =
    useSelector(pageData);

  useEffect(() => {
    // Verificar si hay error de Siscotel (HTTP 200 pero status != 200 en body)
    if (solicitud?.aseguradora === "MyH+" && siscotel && siscotel.siscotelStatus && siscotel.siscotelStatus !== 200) {
      setLoadingx(false);
      isSubmittingRef.current = false;
      toast.error(`Error en MyH+: ${siscotel.siscotelAnswer || "Error desconocido"}`);
      dispatch(resetSiscotelFlag());
      return;
    }

    // Solo procesar si hay una respuesta exitosa y estamos en proceso de envío
    if (solicitud?.aseguradora === "MyH+" && siscotel && siscotel.status == 200 && siscotel.isSuccess !== false && isSubmittingRef.current) {
      const currentMensaje = mensajeRef.current;
      const currentAdjunto = adjuntoRef.current;

      if (!currentMensaje || currentMensaje.trim() === "") {
        isSubmittingRef.current = false;
        dispatch(resetSiscotelFlag());
        return;
      }

      const comentario = {
        tipo: "Enviado",
        mensaje: currentMensaje.trim(),
        estatus: true,
        adjunto: currentAdjunto,
        creado: null,
      };

      // Make sure identificador exists
      if (!solicitud.identificador) {
        toast.error("Error: Identificador no encontrado");
        isSubmittingRef.current = false;
        dispatch(resetSiscotelFlag());
        return;
      }

      // Asegurarse de que el identificador sea una cadena
      const idString = String(solicitud.identificador);

      // Resetear el guard ANTES de dispatchar para evitar que el re-disparo
      // del efecto (por cambio de `solicitud` tras solicitudSuccess dentro
      // del thunk) vuelva a dispatchar addComentario y duplique el comentario.
      isSubmittingRef.current = false;

      dispatch(addComentario({ identificador: idString, comentario }))
        .then(() => {
          setMensaje("");
          setAdjunto(null);
          setLoadingx(false);
          toast.success("Mensaje enviado exitosamente");
          dispatch(resetSiscotelFlag());
        })
        .catch((error) => {
          setLoadingx(false);
          toast.error("Error al guardar el comentario");
          dispatch(resetSiscotelFlag());
        });
    }
  }, [siscotel, solicitud, dispatch]);

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      const maxSize = 1024 * 1024; // 1MB en bytes
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!file) {
        return;
      }

      // Validar tipo de archivo
      if (!allowedTypes.includes(file.type)) {
        alert("Solo se permiten archivos JPG, PNG y PDF");
        e.target.value = ""; // Limpiar el input
        return;
      }

      // Validar tamaño
      if (file.size > maxSize) {
        alert("El archivo no debe superar 1MB");
        e.target.value = ""; // Limpiar el input
        return;
      }

      // Procesar el archivo
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          let finalData = reader.result;

          // Si es una imagen, comprimir
          if (file.type.startsWith("image/")) {
            finalData = await compressImage(reader.result);
          }

          setAdjunto(finalData);
        } catch (error) {
          //console.error("Error procesando archivo:", error);
          alert("Error al procesar el archivo. Por favor, intente nuevamente.");
          e.target.value = "";
        }
      };

      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setUploadProgress(progress);
        }
      };

      reader.onerror = () => {
          console.error("Error leyendo archivo");
        alert("Error al leer el archivo. Por favor, intente nuevamente.");
        e.target.value = "";
      };

      reader.readAsDataURL(file);
    } catch (error) {
      //console.error("Error en handleFileChange:", error);
      alert("Error al procesar el archivo. Por favor, intente nuevamente.");
      e.target.value = "";
    }
  };

  const resetForm = () => {
    setMensaje("");
    setAdjunto(null);
    setLoadingx(false);
    setUploadProgress(0);
    setMensajeError("");

    // Limpiar el input file
    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmitComentario = async () => {
    try {
      setLoadingx(true);

      if (!mensaje || mensaje.trim() === "") {
        setMensajeError("El mensaje es obligatorio");
        setLoadingx(false);
        return;
      }

      setMensajeError("");

      let adjuntoProcessed = null;
      if (adjunto) {
        if (adjunto.startsWith("data:image")) {
          try {
            adjuntoProcessed = await compressImage(adjunto);
          } catch (error) {
            //console.error("Error procesando adjunto:", error);
            throw new Error("Error al procesar el archivo adjunto");
          }
        }
        adjuntoProcessed = cleanBase64(adjunto);
      }

      const esMercantil = solicitud.aseguradora === "Seguros Mercantil";
      const esLatinaSalud = solicitud?.aseguradora?.toLowerCase() === "latina salud";
      const esHispana = solicitud?.aseguradora?.toLowerCase() === "hispana de seguros";
      const esRealSeguros = solicitud?.aseguradora?.toLowerCase() === "real seguros";


      if (esMercantil || esLatinaSalud || esHispana || esRealSeguros) {
        const comentario = {
          tipo: "Enviado",
          mensaje: mensaje.trim(),
          estatus: true,
          adjunto: adjuntoProcessed || null,
          creado: null,
        };

        try {
          await dispatch(
            addComentario({
              identificador: String(solicitud.identificador),
              comentario,
            })
          );
          setMensaje("");
          setAdjunto(null);
          toast.success("Mensaje enviado exitosamente");
        } catch (error) {
          toast.error("Error al enviar el mensaje. Por favor, intente nuevamente.");
        }
        setLoadingx(false);
      } else {
        // Marcar que estamos en proceso de envío para MyH+
        isSubmittingRef.current = true;

        const message = {
          IdentificadorEvento: solicitud.identificador,
          Tipo: "Mensaje",
          Mensaje: mensaje,
          Adjunto: adjuntoProcessed || "",
          CodigoProveedor: solicitud.codigoProveedor,
        };

        try {
          await dispatch(createMessage(message));
          // No limpiar loadingx aquí - el useEffect se encargará después de procesar siscotel
        } catch (error) {
          setLoadingx(false);
          isSubmittingRef.current = false;
          const errorMsg = error?.message || "Error al enviar el mensaje";
          toast.error(`Error en MyH+: ${errorMsg}`);
        }
      }
    } catch (error) {
      setLoadingx(false);
      const errorMsg = error?.message || "Error al procesar el mensaje";
      toast.error(`Error: ${errorMsg}`);
    }
  };

  const formatDate = (dateString) => {
    // Verificar el formato de la fecha
    const isZuluTime = dateString.endsWith("Z");
    const date = new Date(dateString);

    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      // Si es Zulu time (termina en Z), usar UTC, si no, usar America/Bogota
      timeZone: isZuluTime ? "UTC" : "America/Caracas",
    });
  };

  // Función para refrescar los comentarios
  const refreshComments = useCallback(async () => {
    if (!solicitud || !solicitud.id) return;
    
    try {
      setIsRefreshing(true);
      
      // Realizar la solicitud directamente al API en lugar de usar el dispatch
      const api = new APIClient();
      const response = await api.get(`/solicitud/${solicitud.id}`);
      
      if (response && response.data) {
        // Actualizar manualmente los comentarios en el estado local
        const updatedSolicitud = {
          ...solicitud,
          comentarios: response.data.data.comentarios || []
        };
        
        // Actualizar el estado global a través de Redux
        dispatch(solicitudSuccess({
          data: {
            data: updatedSolicitud
          },
          status: 200
        }));
        
      }
      
      setLastUpdateTime(Date.now());
    } catch (error) {
        //console.error("Error al actualizar comentarios:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [solicitud, dispatch]);

  // Nota: Se eliminó la actualización automática cada 30 segundos
  // porque causaba que toda la página se recargara y se perdiera el estado
  // de otros componentes. Ahora solo se actualiza manualmente con el botón "Actualizar".

  // Función para marcar comentarios como leídos manualmente
  const handleMarcarComentariosLeidos = async () => {
    if (!solicitud || !solicitud.id) {
      console.error("No hay solicitud o ID válido");
      return;
    }
    
    try {
      const response = await marcarComentariosLeidos(solicitud.id);
      
      if (response && (response.status === 200 || response.data)) {
        toast.success("Comentarios marcados como leídos");
        // Refrescar para actualizar la UI inmediatamente
        await refreshComments();
      } else {
        console.error("Respuesta inesperada:", response);
        toast.error("Error al marcar comentarios como leídos");
      }
    } catch (error) {
      console.error("Error al marcar comentarios como leídos:", error);
      toast.error("Error al marcar comentarios como leídos");
    }
  };

  // Calcular cantidad de comentarios no leídos
  const comentariosNoLeidos = solicitud.comentarios?.filter(
    c => c.estatus === true && c.tipo !== "Enviado"
  ).length || 0;

  return (
    <React.Fragment>
      <Col>
        <div
          className="comentarios-container"
          style={{ maxWidth: "100%", overflowX: "hidden" }}
          data-saving={loadingx}
        >
          <Card>
            <CardBody className="p-4">
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title mb-0">Comentarios</h5>
                    {/* Badge de comentarios nuevos */}
                    {comentariosNoLeidos > 0 && (
                      <span className="badge bg-danger">
                        {comentariosNoLeidos} nuevo{comentariosNoLeidos > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {/* Botón para marcar como leídos */}
                    {comentariosNoLeidos > 0 && (
                      <Button 
                        color="soft-success" 
                        size="sm"
                        onClick={handleMarcarComentariosLeidos}
                        title="Marcar todos como leídos"
                      >
                        <i className="ri-check-double-line me-1"></i>
                        Marcar leídos
                      </Button>
                    )}
                    {/* Botón actualizar */}
                    <Button 
                      color="soft-info" 
                      size="sm"
                      onClick={refreshComments}
                      disabled={isRefreshing}
                    >
                      <i className={`ri-refresh-line ${isRefreshing ? 'spin' : ''} me-1`}></i>
                      {isRefreshing ? 'Actualizando...' : 'Actualizar'}
                    </Button>
                  </div>
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    Última actualización: {new Date(lastUpdateTime).toLocaleTimeString()}
                  </small>
                </div>
              </div>
              <SimpleBar
                style={{ maxHeight: "500px", height: "auto" }}
                className="px-3 mx-n3"
              >
                {solicitud.comentarios && solicitud.comentarios.length > 0 ? (
                  [...solicitud.comentarios]
                    .reverse()
                    .map((comentario, index) => (
                      <div 
                        className={`d-flex mb-4 ${comentario.estatus === true && comentario.tipo !== "Enviado" ? 'p-3 rounded border border-danger border-opacity-25 bg-danger bg-opacity-10 overflow-hidden' : ''}`} 
                        key={index}
                      >
                        <div className="flex-shrink-0">
                          <div 
                            className={`avatar-xs rounded-circle me-3 d-flex align-items-center justify-content-center ${
                              comentario.tipo === "Enviado" 
                                ? "bg-primary-subtle text-primary" 
                                : "bg-success-subtle text-success"
                            }`}
                          >
                            <i className={`${
                              comentario.tipo === "Enviado" 
                                ? "ri-send-plane-fill" 
                                : "ri-mail-download-fill"
                            } fs-14`}></i>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3 overflow-hidden">
                          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                            <h5 className="fs-13 mb-0 me-2">
                              {comentario.tipo === "Enviado" ? (
                                <span className="text-primary">Farmacia</span>
                              ) : (
                                <span className="text-success">
                                  {solicitud.solicitante}
                                </span>
                              )}
                            </h5>
                            <small className="text-muted">
                              {formatDate(comentario.creado) || ""}
                            </small>
                            {/* Badge "Nuevo" para comentarios no leídos del cliente */}
                            {comentario.estatus === true && comentario.tipo !== "Enviado" && (
                              <span className="badge bg-soft-danger text-danger ms-2 fs-10">
                                <i className="ri-mail-unread-line me-1"></i>Nuevo
                              </span>
                            )}
                          </div>
                          <div
                            className={`p-3 rounded ${
                              comentario.tipo === "Enviado"
                                ? "bg-light-subtle"
                                : "bg-light"
                            }`}
                            style={{ wordBreak: "break-word" }}
                          >
                            <p className="mb-0">{comentario.mensaje}</p>
                          </div>

                          {comentario.adjunto && (
                            <Button
                              color="link"
                              className="p-0 text-info"
                              onClick={(e) => {
                                e.preventDefault();
                                handleOpenFile(comentario.adjunto);
                              }}
                            >
                              <i
                                className={`me-1 ${
                                  isPdfFile(comentario.adjunto)
                                    ? "ri-file-pdf-line"
                                    : isImageFile(comentario.adjunto)
                                    ? "ri-image-line"
                                    : "ri-file-line"
                                }`}
                              ></i>
                              {isPdfFile(comentario.adjunto)
                                ? "Ver PDF"
                                : isImageFile(comentario.adjunto)
                                ? "Ver Imagen"
                                : "Ver Adjunto"}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="ri-chat-off-line fs-24 mb-2 d-block"></i>
                    <p className="mb-0">
                      No hay comentarios para esta solicitud.
                    </p>
                  </div>
                )}
              </SimpleBar>

              {solicitud &&
              solicitud.estatus !== "Cancelado" &&
              solicitud.estatus !== "Entregado" ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmitComentario();
                  }}
                  className="mt-3"
                >
                  <Row className="g-3">
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="mensaje" className="form-label">
                          Mensaje <span className="text-danger">*</span>
                        </Label>
                        <textarea
                          className={`form-control ${
                            mensajeError ? "is-invalid" : ""
                          }`}
                          id="mensaje"
                          rows="3"
                          value={mensaje}
                          onChange={(e) => {
                            setMensaje(e.target.value);
                            if (e.target.value.trim() !== "") {
                              setMensajeError("");
                            }
                          }}
                          placeholder="Escribe tu mensaje aquí..."
                          maxLength={1000}
                        ></textarea>
                        {mensajeError && (
                          <div className="invalid-feedback">{mensajeError}</div>
                        )}
                        <small className="text-muted d-block text-end mt-1">
                          {mensaje.length}/1000 caracteres
                        </small>
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="mb-3">
                        <Label htmlFor="fileInput" className="form-label">
                          Adjuntar archivo (opcional)
                        </Label>
                        <div 
                          className="border border-dashed rounded p-4 text-center"
                          style={{ 
                            borderColor: '#e9ebec',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.borderColor = '#405189';
                            e.currentTarget.style.backgroundColor = '#f3f6f9';
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e9ebec';
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.borderColor = '#e9ebec';
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            const file = e.dataTransfer.files[0];
                            if (file) {
                              const input = document.getElementById("fileInput");
                              const dataTransfer = new DataTransfer();
                              dataTransfer.items.add(file);
                              input.files = dataTransfer.files;
                              handleFileChange({ target: input });
                            }
                          }}
                          onClick={() => document.getElementById("fileInput").click()}
                        >
                          <input
                            type="file"
                            className="d-none"
                            id="fileInput"
                            onChange={handleFileChange}
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                          />
                          {!adjunto ? (
                            <>
                              <div className="mb-3">
                                <i className="ri-upload-cloud-2-line fs-1 text-muted"></i>
                              </div>
                              <h5 className="fs-15 mb-1">
                                Arrastra o <span className="text-primary">selecciona</span> un archivo
                              </h5>
                              <p className="text-muted mb-0">
                                <i className="ri-information-line me-1"></i>
                                Formatos aceptados: Imágenes (JPG, PNG, WEBP) o PDF. Tamaño máximo: 2MB
                              </p>
                            </>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center">
                              <div className="flex-grow-1">
                                <i className="ri-file-line fs-1 text-success me-2"></i>
                                <span className="text-success fw-medium">
                                  Archivo cargado correctamente
                                </span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAdjunto(null);
                                  document.getElementById("fileInput").value = "";
                                }}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          )}
                          {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="progress mt-3" style={{ height: "8px" }}>
                              <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: `${uploadProgress}%` }}
                                aria-valuenow={uploadProgress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col lg={12} className="text-end">
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={loadingx}
                      >
                        {loadingx ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <i className="ri-discuss-line align-middle fs-16 me-2"></i>
                            Enviar
                          </>
                        )}
                      </button>
                    </Col>
                  </Row>
                </form>
              ) : (
                <div className="alert alert-info mt-3">
                  <i className="ri-information-line me-2"></i>
                  No es posible añadir comentarios a esta solicitud.
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </Col>

      <FileViewer 
        isOpen={fileViewerOpen}
        toggle={() => setFileViewerOpen(!fileViewerOpen)}
        fileUrl={currentFile.url}
        fileName={currentFile.name}
        fileType={currentFile.type}
      />
    </React.Fragment>
  );
};

export default Comentarios;
